import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { API, authFetch } from "../App";
import PortalLayout from "../components/PortalLayout";
import {
  MessageSquare,
  Clock,
  BookOpen,
  Upload,
  UserPlus,
  Activity
} from "lucide-react";

const ACTIVITY_ICONS = {
  document_upload: { icon: Upload, color: "#3B82F6" },
  message_sent: { icon: MessageSquare, color: "#8B5CF6" },
  transaction_added: { icon: BookOpen, color: "#F59E0B" },
  profile_update: { icon: UserPlus, color: "#10B981" },
};

const TeamActivityPage = () => {
  const [loading, setLoading] = useState(true);
  const [activityFeed, setActivityFeed] = useState([]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/team/activity-feed`);
      if (res.ok) {
        setActivityFeed(await res.json());
      }
    } catch (error) {
      console.error("Error fetching activity feed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <PortalLayout title="Client Activity">
      <div className="relative z-10 w-full pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold mb-1 text-[var(--text-main)]">
            Client Activity
          </h1>
          <p className="text-[var(--text-muted)]">
            A comprehensive feed of recent actions taken by your assigned clients.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="soft-card"
        >
          <div className="flex items-center gap-2 mb-3 border-b border-[var(--border-light)] pb-4">
            <Activity className="w-5 h-5 text-[var(--primary)]" />
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-[var(--text-main)]">Recent Activity</h2>
          </div>
          <div className="space-y-0">
            {activityFeed.length > 0 ? activityFeed.map((event, i) => {
              const { icon: Icon, color } = ACTIVITY_ICONS[event.type] || { icon: Clock, color: "#94A3B8" };
              const timeAgo = event.timestamp ? new Date(event.timestamp).toLocaleString("en-IN", {
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
              }) : "";
              return (
                <div key={i} className="flex gap-4 py-4 border-b border-[var(--border-light)] last:border-0 hover:bg-[var(--background-primary)] transition-colors -mx-4 px-4 rounded-xl">
                  <div className="flex flex-col items-center pt-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm border border-[var(--border-light)] bg-white">
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    {i < activityFeed.length - 1 && <div className="w-px flex-1 mt-3 bg-[var(--border-light)]" />}
                  </div>
                  <div className="flex-1 min-w-0 pb-2">
                    <p className="text-sm font-bold text-[var(--text-main)]">{event.client_name}</p>
                    <p className="text-sm text-[var(--text-muted)] mt-0.5">{event.description}</p>
                    <p className="text-xs font-medium text-[var(--text-muted)] mt-1.5 opacity-80">{timeAgo}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[var(--background-primary)] flex items-center justify-center mx-auto mb-4 border border-[var(--border-light)]">
                  <Clock className="w-8 h-8 text-[var(--text-muted)]" />
                </div>
                <h3 className="font-bold text-[var(--text-main)] text-lg mb-1">No recent activity</h3>
                <p className="text-sm text-[var(--text-muted)]">When your clients take actions, they will appear here.</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </PortalLayout>
  );
};

export default TeamActivityPage;
